import 'feather-icons'
import React, { useEffect, useMemo, useState } from 'react'
import TagManager from 'react-gtm-module'
import { useMedia } from 'react-use'
import styled from 'styled-components'
import { FullWrapper, PageWrapper } from '../components'
import { AutoColumn } from '../components/Column'
import GlobalChart from '../components/GlobalChart'
import GlobalStats from '../components/GlobalStats'
import PairList from '../components/PairList'
import Panel from '../components/Panel'
import { RowBetween } from '../components/Row'
import Title from '../components/Title'
import TopTokenList from '../components/TokenList'
import TxnList from '../components/TxnList'
import { useGlobalTransactions } from '../contexts/GlobalData'
import { useAllPairData } from '../contexts/PairData'
import { useAllTokenData } from '../contexts/TokenData'
import { useTokenList } from '../contexts/TokenList'
import { TYPE } from '../Theme'

const tagManagerArgs = {
  js: new Date(),
  gtmId: process.env.REACT_APP_GTM || ''
}

TagManager.initialize(tagManagerArgs)

const AnalyticTitle = styled.div`
  width: 100%;
  font-size: 40px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #0b1426;
  text-align: center;
`

const GridRow = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: 1fr 1fr;
  column-gap: 6px;
  align-items: start;
  justify-content: space-between;
`

const isSupportedPair = (pair, tokenList) => {
  return tokenList.includes(pair?.token0?.id) && tokenList.includes(pair?.token1?.id)
}

function OverviewPage() {
  const allTokens = useAllTokenData()
  const { tokenList } = useTokenList()
  const allPairs = useAllPairData()
  const transactions = useGlobalTransactions()
  const below800 = useMedia('(max-width: 800px)')
  const [tokenAddressList, setTokenAddressList] = useState([])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    setTokenAddressList(tokenList.map(item => item.address))
  }, [tokenList])

  const supportedTokenMap = useMemo(() => {
    if (!tokenAddressList || tokenAddressList.length === 0) return undefined
    const supportedTokenTempMap = {}
    tokenAddressList.forEach(address => {
      if (allTokens[address]) supportedTokenTempMap[address] = allTokens[address]
    })
    return supportedTokenTempMap
  }, [allTokens, tokenAddressList])

  const supportedPairMap = useMemo(() => {
    if (!tokenAddressList || tokenAddressList.length === 0) return undefined

    const supportedPairTempMap = {}

    for (const [pairAddress, pairDetail] of Object.entries(allPairs)) {
      if (isSupportedPair(pairDetail, tokenAddressList)) supportedPairTempMap[pairAddress] = pairDetail
    }
    return supportedPairTempMap
  }, [allPairs, tokenAddressList])

  const txnMemo = useMemo(() => {
    if (!transactions || !tokenAddressList || tokenAddressList.length === 0) return undefined

    const { mints, burns, swaps } = transactions

    return {
      mints: mints.filter(item => isSupportedPair(item.pair, tokenAddressList)),
      burns: burns.filter(item => isSupportedPair(item.pair, tokenAddressList)),
      swaps: swaps.filter(item => isSupportedPair(item.pair, tokenAddressList))
    }
  }, [transactions, tokenAddressList])

  return (
    <PageWrapper>
      <FullWrapper>
        <Title />
        <AutoColumn gap="24px" style={{ paddingBottom: below800 ? '0' : '24px', marginTop: 40 }}>
          <AnalyticTitle>DeFi Swap Analytics</AnalyticTitle>
          <GlobalStats />
        </AutoColumn>
        {!below800 && (
          <GridRow>
            <Panel style={{ height: '100%', minHeight: '300px' }}>
              <GlobalChart display="liquidity" />
            </Panel>
            <Panel style={{ height: '100%' }}>
              <GlobalChart display="volume" />
            </Panel>
          </GridRow>
        )}
        {below800 && (
          <AutoColumn style={{ marginTop: '6px' }} gap="24px">
            <Panel style={{ height: '100%', minHeight: '300px' }}>
              <GlobalChart display="liquidity" />
            </Panel>
          </AutoColumn>
        )}

        <RowBetween>
          <TYPE.largeHeader>Tokens</TYPE.largeHeader>
        </RowBetween>
        <Panel style={{ marginTop: '6px', padding: below800 && '1rem 0 0 0 ' }}>
          <TopTokenList tokens={supportedTokenMap} tokenList={tokenList} itemMax={10} />
        </Panel>

        <RowBetween>
          <TYPE.largeHeader>Pairs</TYPE.largeHeader>
        </RowBetween>
        <Panel style={{ padding: below800 && '1rem 0 0 0 ' }}>
          <PairList pairs={supportedPairMap} disbaleLinks={true} maxItems={10} />
        </Panel>

        <RowBetween>
          <TYPE.largeHeader>Transactions</TYPE.largeHeader>
        </RowBetween>
        <Panel style={{ margin: '1rem 0' }}>
          <TxnList transactions={txnMemo} />
        </Panel>
      </FullWrapper>
    </PageWrapper>
  )
}

export default OverviewPage
